import { IFilter, IFilterValueLocale } from './filters.model';
import { IPagination } from './pagination.model';

export enum CategoriesEndpoints {
  CATEGORIES = '/categories',
  CATEGORIES_AUTOCOMPLETE = '/categories/autocomplete',
  POPULAR_CATEGORIES = '/categories/popular',
  CATEGORY_STATS = '/categories/stats',
  MARKETPLACE_CATEGORIES = '/marketplace/categories',
}

// ----------------- DTOs ----------------

export interface IEditCategoryDTO {
  locale?: ICategoryNameLocale[];
  parent_id?: string;
  slug?: string;
  photo?: File;
  google_feed_id?: number;
  is_restoration?: boolean;
  is_heavy?: boolean | null;
}

export interface ICreateCategoryDTO {
  locale?: ICategoryNameLocale[];
  parent_id?: string;
  google_feed_id?: number;
  photo?: File;
  is_heavy?: boolean | null;
}

export interface IUpdateFiltersCategoryDTO {
  filters?: string[];
}

export interface ICreatePopularCategoriesDTO {
  categories: string[];
}

export interface ICategoriesAutocompleteDTO {
  name?: string;
  page?: number;
  limit?: number;
  has_children?: '1' | '0' | null;
}

// ------------ DTOs (stats) ----------------

export interface IGetCategoryStatsDTO {
  category_id?: string;
  date_from?: string;
  date_to?: string;
}

// ------------ Responses --------------

export interface ICategoryStatsLevel {
  total: number;
  active: number;
  inactive: number;
}

export interface ICategoryContractor {
  id: string;
  title: string;
  product_count: number;
  partner_id: string | null;
}

export interface ICategoryStats {
  level_1: ICategoryStatsLevel;
  level_2: ICategoryStatsLevel;
  level_3: ICategoryStatsLevel;
  sku: ICategoryStatsLevel;
  contractors?: ICategoryContractor[];
}

export interface IGetCategoryStatsResponse {
  data: ICategoryStats;
}

export interface ICategoryMarketplaceMapping {
  marketplace: string;
  external_id: string;
  name?: string;
  path?: string;
  matched_by: 'exact' | 'path' | 'inherited' | 'manual';
}

export interface IMarketplaceCategory {
  marketplace: string;
  external_id: string;
  name: string;
  path: string;
  is_leaf: boolean;
  mapped_category_id?: string | null;
}

export interface IMarketplaceCategoriesResponse {
  data: IMarketplaceCategory[];
}

export interface IUpdateMarketplaceMappingsDTO {
  marketplace: string;
  external_ids: string[];
}

export interface IUpdateMarketplaceMappingsResponse {
  data: ICategoryMarketplaceMapping[];
}

export interface ICategory {
  id: string;
  name: string;
  sub_categories?: ICategory[];
  is_loading?: boolean;
  active?: boolean;
  marketplace_mappings?: ICategoryMarketplaceMapping[];
}

export interface ICategoriesResponse {
  data: ICategory[];
}

export interface ISubCategoriesResponse {
  data: ICategory;
}

export enum CategoryLocaleCodes {
  UK = 'uk',
  RU = 'ru',
}

export interface ICategoryNameLocale {
  locale: string;
  seo_title?: string;
  seo_description?: string;
  keywords?: string[];
  seo_text?: string;
  name?: string;
}

export interface ILocaleData {
  locale: {
    [key: string]: Omit<ICategoryNameLocale, 'locale'>;
  };
}

export interface ICategoryFilter extends IFilter {
  min?: number;
  max?: number;
  sort_order?: number;
  seo_filters?: string[];
}

export interface ICategoryFilterValue {
  id: string;
  filter_id: string;
  is_active: boolean | null;
  slug: string;
  position: number | null;
  is_selected: boolean;
  locale: IFilterValueLocale;
  created_at: string;
  updated_at: string;
}

export interface ICategoryFilterValuesQuery {
  title?: string;
  slug?: string;
  page?: number;
  limit?: number;
  is_selected?: boolean;
}

export interface ICategoryFilterValuesResponse {
  data: ICategoryFilterValue[];
  meta: IPagination;
}

export interface IUpdateCategoryFilterValuesDTO {
  value_ids: string[];
  new_values: { uk: string; ru: string }[];
}

export interface IUpdateCategorySeoFiltersDTO {
  filters: { filter_id: string; value_ids: string[] }[];
}

export interface ICategoryDetails {
  id: string;
  parent_id: string | null;
  name: string;
  photo: string;
  slug: string;
  google_feed_id?: number;
  is_restoration?: boolean;
  is_heavy?: boolean | null;
  active?: boolean;
  locale: ICategoryNameLocale[];
  filters: ICategoryFilter[];
  sub_categories?: ICategory[];
  marketplace_mappings?: ICategoryMarketplaceMapping[];
}

export interface ICategoryDetailsResponse {
  data: ICategoryDetails;
}

export interface IDeleteCategoryResponse {
  data: {
    success: boolean;
  };
}

export interface IEditCategoryResponse {
  data: ICategoryDetails;
}

export interface ICreateCategoryResponse {
  data: ICategoryDetails;
}

export interface IChangeCategoryPosition {
  data: {
    success: true;
  };
}
